import React, { memo, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Card, Spin } from 'antd'
import ReactEcharts from 'echarts-for-react'
import { useTranslation } from 'react-i18next'
import './UsageByService.scss'
const UsageByService = memo(({ data, loading }) => {
  const { t } = useTranslation()

  const chartData = useMemo(() => {
    if (!Array.isArray(data?.serviceUsage) || data.serviceUsage.length === 0) return null

    const colorMap = {
      image: '#5B6EE2',
      ai_video: '#C9D3FA',
      audio: '#3875F6',
      course: '#44959A',
      podcast: '#EB3B88',
      maze: '#9FD4FB',
      sharebot: '#2DCCB9'
    }

    const services = data.serviceUsage.map(item => {
      const type = item.type?.toLowerCase()
      const percent = item.percent !== undefined ? item.percent : 0

      const displayPercent = typeof percent === 'number'
        ? (Number.isInteger(percent) ? percent : percent.toFixed(1))
        : 0

      return {
        name: item.type.charAt(0).toUpperCase() + item.type.slice(1),
        value: item.credits || 0,
        percentage: displayPercent,
        color: colorMap[type] || '#888'
      }
    })

    return {
      services,
      total: services.reduce((sum, item) => sum + item.value, 0)
    }
  }, [data])

  const chartOption = useMemo(() => {
    if (!chartData || chartData.services.length === 0) return null

    const visibleServices = chartData.services.filter(service => service.value > 0)

    if (visibleServices.length === 0) return null

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          const service = chartData.services.find(s => s.name === params.name)
          return `${params.name?.replace('_', ' ')}: ${params.value} (${service?.percentage}%)`
        }
      },
      series: [
        {
          name: 'Usage by Service',
          type: 'pie',
          radius: ['55%', '78%'],
          center: ['48%', '50%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 6,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: false
          },
          emphasis: {
            scale: true,
            label: {
              show: false
            }
          },
          data: visibleServices.map(item => ({
            value: item.value,
            name: item.name,
            itemStyle: {
              color: item.color
            }
          }))
        }
      ]
    }
  }, [chartData])

  if (!data && !loading) {
    return null
  }

  return (
    <Card className="usage-by-service-card">
      <Spin spinning={loading}>
        <div className="usage-by-service-content">

          <div className="chart-and-table">
            <div className="chart-container">
              {chartOption ? (
                <ReactEcharts
                  option={chartOption}
                  className="echarts-wrapper"
                  opts={{ renderer: 'svg' }}
                />
              ) : (
                <div className="no-chart-data">No data available</div>
              )}
            </div>

            <div className="service-table">
              <h3 className="section-title">Usage by Service</h3>
              <table>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Credits</th>
                    <th>usage (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {chartData?.services?.map((service, index) => (
                    <tr key={index}>
                      <td>
                        <div className="service-type">
                          <span
                            className="color-dot"
                            style={{ backgroundColor: service.color }}
                          />
                          <span>{service.name?.replace('_', ' ')}</span>
                        </div>
                      </td>
                      <td>{service.value.toLocaleString()}</td>
                      <td>{service.percentage}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Spin>
    </Card>
  )
})

UsageByService.propTypes = {
  data: PropTypes.shape({
    usageByFeature: PropTypes.shape({
      image: PropTypes.number,
      video: PropTypes.number,
      audio: PropTypes.number,
      course: PropTypes.number,
      podcast: PropTypes.number,
      maze: PropTypes.number,
      sharebot: PropTypes.number
    })
  }),
  loading: PropTypes.bool
}

UsageByService.displayName = 'UsageByService'

export default UsageByService
